'use client'
import * as amplitude from '@amplitude/analytics-browser'
import type {
	Plugin,
	BrowserClient,
	BrowserConfig,
} from '@amplitude/analytics-browser/lib/esm/types'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { getEnvVariable } from '@/app/service/getenvService'
import { extractErrorOrUnknown } from '@/app/lib/errors/errorUtils'

interface SurveyItem {
	id: string
	name?: string
	[key: string]: unknown
}

interface EngagementBootOptions {
	user: {
		user_id: string
		device_id?: string
		user_properties: Record<string, unknown>
	},
	integrations?: Array<{ track: (eventType: string, eventProperties?: Record<string, unknown>) => unknown }>
}

declare global {
	interface Window {
		dataLayer: Record<string, unknown>[]
		engagement?: {
			plugin: () => Plugin<BrowserClient, BrowserConfig>
			boot?: (options: EngagementBootOptions) => void
			gs?: {
				show: (key: string) => void
				closeAll: () => void
				list: () => SurveyItem[]
			}
			surveys?: {
				show: (key: string) => void
				close: () => void
				list: () => SurveyItem[]
			}
			on?: (event: string, callback: (event: unknown) => void) => void
			_debugStatus?: () => void
		}
	}
}

export interface AnalyticsEvent {
	event?: string
	eventParameter?: string
	eventParameterValue?: string
	[key: string]: string | undefined
}

let initialized = false
let initPromise: Promise<void> | null = null
let pluginAdded = false
let pluginPromise: Promise<void> | null = null

const addEngagementPluginWithRetry = (resolve: () => void, retries = 0): void => {
	if (pluginAdded) {
		resolve()
		return
	}

	if (window.engagement) {
		try {
			const pluginInstance = window.engagement.plugin()
			amplitude.add(pluginInstance)
			pluginAdded = true
		} catch (error) {
			const normalized = extractErrorOrUnknown(
				error,
				'Failed to add Amplitude Engagement plugin'
			)
			console.warn('Failed to add Amplitude Engagement plugin:', normalized.message)
		}
		resolve()
		return
	}

	// Retry for up to 5 seconds (20 retries × 250ms)
	if (retries < 20) {
		setTimeout(() => addEngagementPluginWithRetry(resolve, retries + 1), 250)
		return
	}

	console.warn('Amplitude engagement script not found after 5s')
	resolve()
}

const ensureEngagementPlugin = async (): Promise<void> => {
	if (pluginAdded) return
	pluginPromise ??= new Promise(resolve => {
		addEngagementPluginWithRetry(resolve)
	})
	await pluginPromise
	if (!pluginAdded) pluginPromise = null
}

export function initAmplitudeOnce(): Promise<void> {
	if (typeof window === 'undefined') {
		return Promise.resolve()
	}

	if (initPromise) {
		return initPromise
	}

	initPromise ??= (async () => {
		if (initialized) return

		const apiKey = await getEnvVariable('NEXT_PUBLIC_AMPLITUDE_API_KEY')

		if (!apiKey) {
			console.warn('Amplitude API key not found')
			return
		}

		amplitude.init(apiKey, undefined, {
			defaultTracking: {
				sessions: true,
				pageViews: true,
				formInteractions: true,
				fileDownloads: true,
			},
		})

		const sampleRateEnv = await getEnvVariable('NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE')
		const parsed = sampleRateEnv !== null ? Number(sampleRateEnv) : null
		const sampleRate = parsed !== null && !Number.isNaN(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 0.5

		amplitude.add(sessionReplayPlugin({ sampleRate }))

		initialized = true

		// Add Guides and Surveys plugin with retry logic.
		await ensureEngagementPlugin()
	})()

	return initPromise
}

// Start polling for window.engagement at module load time so the plugin is
// queued in amplitude before amplitude.init() runs. amplitude.add() accepts
// plugins before init() and initializes them — with the correct device ID —
// when init() is eventually called, regardless of which arrives first.
if (typeof window !== 'undefined') {
	ensureEngagementPlugin()
}

export const pushToDataLayer = (
	analyticsEvent: AnalyticsEvent,
	errorEvent?: boolean
) => {
	const { event, ...eventOptions } = analyticsEvent
	try {
		if (errorEvent && event) {
			amplitude.track(event, { ...eventOptions, errorEvent })
		} else if (event) {
			amplitude.track(event, eventOptions)
		}
	} catch (error) {
		const normalized = extractErrorOrUnknown(
			error,
			'Failed to track event to Amplitude'
		)
		console.warn('Failed to track event to Amplitude:', normalized.message)
	}
}

/**
 * Pushes user identity details to the analytics system.
 */
export const pushUserDetails = async (
	oktaId: string,
	companyId: string,
	brandCode: string,
	affiliationTypeCd: string,
	repId?: string | null
) => {
	if (!oktaId) {
		return
	}

	try {
		// Wait for initialization and plugin registration
		await initAmplitudeOnce()

		const identity = new amplitude.Identify()
			.set('oktaId', oktaId)
			.set('companyId', companyId)
			.set('brandCode', brandCode)
			.set('affiliationTypeCd', affiliationTypeCd)

		if (repId) {
			identity.set('repId', repId)
		}

		amplitude.identify(identity)
		amplitude.setUserId(oktaId)
		amplitude.flush()
	} catch (error) {
		console.warn('Failed to push user details to Amplitude:', error)
	}
}
